'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import type { MagazineArticleListResolved } from './types';
import { ArticleTeaserCard } from '@/components/magazine/ArticleTeaserCard';
import { CategoryFilterBar } from '@/components/magazine/CategoryFilterBar';
import { listMagazineArticles, type MagazineArticleTeaser } from '@/lib/api';
import { useOptionalRenderContext } from '@/lib/render/RenderContextProvider';

interface LoadedBatches {
  /** The filter the batches below were loaded under. */
  key: string;
  items: MagazineArticleTeaser[];
  failed: boolean;
}

const EMPTY_BATCHES: LoadedBatches = { key: '', items: [], failed: false };

/**
 * The grid, its filter bar and its load-more button. The first batch and the
 * page size are resolved backend-side from the request's tag bag; this only
 * appends later batches.
 */
export const MagazineArticleListComponent = ({
  resolved,
}: {
  resolved: MagazineArticleListResolved;
}) => {
  const t = useTranslations('magazine');
  const locale = useLocale();
  const ctx = useOptionalRenderContext();
  // Keyed by the filter it was loaded under: the category pills navigate
  // between two `kategorie/[slug]` URLs, which React reconciles as the same
  // component — without the key, one category's extra batches would stay in
  // the grid under the next one and shift every later offset.
  const [loaded, setLoaded] = useState<LoadedBatches>(EMPTY_BATCHES);
  const [loading, setLoading] = useState(false);

  const { filter, total, categories, exclude, numberOfItems } = resolved;
  const filterKey =
    filter.kind === 'none' ? 'none' : `${filter.kind}:${filter.slug}`;
  const current = loaded.key === filterKey ? loaded : EMPTY_BATCHES;
  const items = [...resolved.items, ...current.items];
  const hasMore = items.length < total;
  const domainTag = ctx?.tagBag.find((tag) => tag.startsWith('domain:'));
  const offset = items.length;

  const loadMore = useCallback(async () => {
    if (!domainTag) {
      return;
    }
    setLoading(true);
    try {
      const batch = await listMagazineArticles({
        locale,
        domain: domainTag.slice('domain:'.length),
        category: filter.kind === 'category' ? filter.slug : undefined,
        author: filter.kind === 'author' ? filter.slug : undefined,
        // The same exclusion the backend used — dropping it would let the
        // featured article reappear and shift every later offset by one.
        exclude: exclude ?? undefined,
        limit: numberOfItems,
        offset,
      });
      setLoaded((previous) => ({
        key: filterKey,
        items:
          previous.key === filterKey
            ? [...previous.items, ...batch.items]
            : batch.items,
        failed: false,
      }));
    } catch {
      // The batch is lost, not the page: the grid keeps what it has and the
      // button stays, so the reader can retry without a reload.
      setLoaded((previous) => ({
        key: filterKey,
        items: previous.key === filterKey ? previous.items : [],
        failed: true,
      }));
    } finally {
      setLoading(false);
    }
  }, [domainTag, exclude, filter, filterKey, locale, numberOfItems, offset]);

  // Nothing was resolved for this page at all — no articles, no pills and no
  // filter. That is what the backend resolver returns when the render carries
  // no magazine context: the preview path, and any Page off the magazine routes
  // that happens to carry this block (spec 10.03.02.02.04 § AC 2, last bullet).
  // An author with no articles in this locale still renders its heading over an
  // empty grid, because its filter is not `none`.
  if (total === 0 && categories.length === 0 && filter.kind === 'none') {
    return null;
  }

  const authorName = ctx?.entities.author?.name;

  return (
    <section className="py-6">
      {filter.kind === 'author' ? (
        // Only with a name: `Artikel von ` with nothing after it reads worse
        // than no heading at all, and the name is what the heading is for.
        // Wave 4 populates `entities.author`.
        authorName ? (
          <h2 className="mb-6 text-center text-2xl font-semibold">
            {t('articlesByAuthor', { name: authorName })}
          </h2>
        ) : null
      ) : (
        <CategoryFilterBar categories={categories} filter={filter} />
      )}

      {items.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((article) => (
            <ArticleTeaserCard key={article.documentId} article={article} />
          ))}
        </div>
      )}

      {current.failed && (
        <p role="alert" className="mt-6 text-center text-sm">
          {t('loadMoreError')}
        </p>
      )}

      {hasMore && (
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loading}
            aria-busy={loading}
            className="border-primary text-primary rounded-full border px-6 py-3 font-light disabled:opacity-60"
          >
            {t('loadMore')}
          </button>
        </div>
      )}
    </section>
  );
};
